'use client'

import { Tab } from 'fukku/Tabs/Tab/Tab'
import { KEYBOARD_KEYS } from 'fukku/Tabs/constants/constants'
import type { TabsProps } from 'fukku/Tabs/types/Tabs'
import { findTabIndexById } from 'fukku/Tabs/utils/utils'
import { useEffect, useRef, useState } from 'react'
import { cx } from 'utils/cx'

import styles from './Tabs.module.scss'

export function Tabs({
	className,
	data,
	'data-testid': dataTestId = 'tabs',
	iconOnly,
	onSelectedTab = () => {},
}: TabsProps): React.ReactNode {
	const [activeTabId, setActiveTabId] = useState<string | null>(null)
	const contentRef = useRef<HTMLDivElement | null>(null)
	const tabsRef = useRef<HTMLButtonElement[]>([])

	/**
	 * Sets default active tab when none is selected
	 */
	const initDefaultTab = () => {
		const activeItem = data.find((item) => item.active)
		if (!activeTabId && activeItem) {
			setActiveTabId(activeItem.id)
		}
	}
	/**
	 * Handles the selection of a tab.
	 */
	const changeTab = (tabId: string) => {
		const urlTabIndex = findTabIndexById(data, tabId) ?? -1

		if (urlTabIndex === -1) {
			initDefaultTab()
			return
		}

		const activeTab = data[urlTabIndex]
		if (activeTab.id !== activeTabId) {
			setActiveTabId(activeTab.id)
			onSelectedTab(activeTab, urlTabIndex)
		}
	}

	/**
	 * Navigates to the selected tab.
	 */
	const navigateTab = (id: string) => {
		window.history.replaceState(null, '', `#${id}`)
		changeTab(id)
	}

	/**
	 * Gets next tab button element depending on pressed key (left/right arrow, home or end)
	 */
	const getNextTab = (key: string, currentActiveId: string) => {
		const tabsList = tabsRef.current
		if (key === KEYBOARD_KEYS.Home) {
			return tabsList[0]
		}
		if (key === KEYBOARD_KEYS.End) {
			return tabsList[tabsList.length - 1]
		}
		const tabIndex = findTabIndexById(data, currentActiveId)
		const newStep =
			key === KEYBOARD_KEYS.ArrowLeft ? tabIndex - 1 : tabIndex + 1
		return (
			tabsList[newStep] ??
			(key === KEYBOARD_KEYS.ArrowLeft
				? tabsList[tabsList.length - 1]
				: tabsList[0])
		)
	}

	/**
	 * Handles keydown events for a11y purposes.
	 */
	const handleKeyDown = (event: React.KeyboardEvent) => {
		const { key } = event
		if (KEYBOARD_KEYS[key] && activeTabId) {
			event.preventDefault()
			const nextTab = getNextTab(key, activeTabId)
			nextTab.focus()
			navigateTab(nextTab.id)
		}
	}

	useEffect(() => {
		changeTab(document.location?.hash?.replace('#', ''))
	}, [])

	return (
		<div className={cx(styles.wrapper, className)}>
			<div
				className={styles.tabs}
				data-active={activeTabId}
				data-testid={`${dataTestId}.list`}
				role='tablist'
			>
				{data.map((tab, idx) => (
					<Tab
						key={tab.id}
						ref={(el: HTMLButtonElement) => (tabsRef.current[idx] = el)}
						data={tab}
						iconOnly={iconOnly}
						isSelected={tab.id === activeTabId}
						onClick={() => navigateTab(tab.id)}
						onKeyDown={handleKeyDown}
					/>
				))}
			</div>
			<div
				ref={contentRef}
				data-testid={`${dataTestId}.content`}
				className={styles.content}
			>
				{data.map((tab) => (
					<div
						key={`${tab.id}.panel`}
						id={`${tab.id}.panel`}
						role='tabpanel'
						aria-labelledby={tab.id}
						className={cx(tab.id !== activeTabId && styles.hidden)}
					>
						{tab.content}
					</div>
				))}
			</div>
		</div>
	)
}
